import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API, SHOW_PRICING } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Star, Loader2, Rocket, Target, Sparkles, Lock } from 'lucide-react';

const FutureBrand = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [reveal, setReveal] = useState(null);

  const tier = user?.subscription_tier || 'free';
  const subscriptionStatus = user?.subscription_status || 'inactive';
  const isBetaUser = user?.is_beta_user || user?.beta_access || subscriptionStatus === 'beta';
  // During beta (SHOW_PRICING=false), all users have access
  const hasAccess = !SHOW_PRICING || tier === 'pro' || tier === 'experience' || isBetaUser;

  useEffect(() => {
    if (hasAccess) fetchLatest();
  }, [hasAccess]);

  const fetchLatest = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/future-brand/latest`, { headers });
      if (response.data.reveal) setReveal(response.data.reveal);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/future-brand/generate`, {}, { headers });
      setReveal(response.data);
      toast.success('Future Brand Reveal generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate');
    } finally {
      setLoading(false);
    }
  };

  // During beta, show gentle message instead of paywall
  if (!hasAccess) {
    return (
      <DashboardLayout title="Future Brand Reveal">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-premium p-12 text-center">
            <Sparkles className="text-[#D4AF37] mx-auto mb-6" size={64} />
            <h2 className="font-serif text-2xl text-white mb-4">Bientôt disponible</h2>
            <p className="text-[#A1A1AA] mb-8 max-w-md mx-auto">
              Cette fonctionnalité sera bientôt accessible. Continue ton parcours pour débloquer de nouvelles possibilités.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="btn-gold px-8 py-4" data-testid="back-btn">
              Retour au parcours
            </Button>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Future Brand Reveal">
      <div className="max-w-4xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-premium p-8 mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <Star className="text-[#E5C585] mb-4" size={28} />
              <h2 className="font-serif text-2xl text-white mb-2">Discover Your Future Brand</h2>
              <p className="text-[#A1A1AA]">See the visionary brand you're becoming.</p>
            </div>
            <Button onClick={handleGenerate} disabled={loading} className="btn-gold px-8 py-4 flex items-center gap-2" data-testid="generate-reveal-btn">
              {loading ? (<><Loader2 className="animate-spin" size={18} />Revealing...</>) : reveal ? 'Regenerate' : 'Reveal Future Brand'}
            </Button>
          </div>
        </motion.div>

        {reveal && (
          <div className="space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="card-premium p-8 text-center border-[#D4AF37]/50">
              <span className="text-[#E5C585] text-xs font-accent tracking-widest">YOUR FUTURE BRAND</span>
              <h2 className="font-serif text-4xl md:text-5xl text-gold-gradient mt-4 mb-6">{reveal.future_brand_name}</h2>
              <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto leading-relaxed">{reveal.evolution_narrative}</p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="card-premium p-6">
                <Target className="text-[#D4AF37] mb-3" size={24} />
                <h3 className="font-serif text-lg text-white mb-3">Elevated Positioning</h3>
                <p className="text-[#A1A1AA]">{reveal.elevated_positioning}</p>
              </motion.div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="card-premium p-6">
                <Sparkles className="text-[#E5C585] mb-3" size={24} />
                <h3 className="font-serif text-lg text-white mb-3">Brand Energy</h3>
                <p className="text-[#A1A1AA]">{reveal.brand_energy}</p>
              </motion.div>
            </div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="card-premium p-6">
              <h3 className="font-serif text-lg text-white mb-4">Signature Offerings</h3>
              <div className="space-y-3">
                {reveal.signature_offerings?.map((offering, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 bg-[#1C1C1C] border border-[#27272A]">
                    <span className="text-[#D4AF37] font-serif">{i + 1}</span>
                    <span className="text-[#A1A1AA]">{offering}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="card-premium p-6">
              <Rocket className="text-[#D4AF37] mb-3" size={24} />
              <h3 className="font-serif text-lg text-white mb-3">Impact Statement</h3>
              <p className="text-[#A1A1AA] text-lg">{reveal.impact_statement}</p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }} className="card-premium p-8 text-center bg-gradient-to-b from-[#121212] to-[#0A0A0A] border-[#D4AF37]/30">
              <span className="text-[#E5C585] text-xs font-accent tracking-widest">YOUR AFFIRMATION</span>
              <p className="font-serif text-xl md:text-2xl text-white mt-4 italic">"{reveal.affirmation}"</p>
            </motion.div>
          </div>
        )}

        {!reveal && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-premium p-16 text-center">
            <Star className="text-[#27272A] mx-auto mb-6" size={80} />
            <h3 className="font-serif text-2xl text-white mb-4">Ready to See Your Future?</h3>
            <p className="text-[#A1A1AA] max-w-md mx-auto">Click the button above to reveal the visionary brand you're becoming.</p>
          </motion.div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default FutureBrand;
